import { useState, useEffect } from 'react';
import { X, Users, Loader2, Check } from 'lucide-react';
import api from '../../services/api';
import { toast } from 'sonner';

const ORANGE = '#ee7203';

/**
 * Multi-select modal so a manager can assign a project to one or more team members.
 *
 * Props:
 *   project   - { id, name, stage, encon_value, barbour_value, crm_url }
 *   user      - current logged-in manager
 *   onClose   - fn()
 *   onSaved   - fn()
 */
export default function AssignProjectToUsersModal({ project, user, onClose, onSaved }) {
  const [teamMembers, setTeamMembers] = useState([]);  // { id, display_name, username, email }
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [initialAssigned, setInitialAssigned] = useState(new Set());
  const [selected, setSelected] = useState(new Set());
  const [saving, setSaving] = useState(false);

  // Load team members + current assignments in parallel
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [teamsRes, assignmentsRes] = await Promise.all([
          api.get('/api/teams'),
          api.get(`/api/project-tracking/project-assignments/${project.id}`),
        ]);

        if (cancelled) return;

        // Collect unique team members across all teams
        const teams = teamsRes.data.teams || [];
        const memberMap = new Map();
        for (const team of teams) {
          const ids = team.member_user_ids || [];
          const names = team.member_names || [];
          ids.forEach((id, idx) => {
            if (!memberMap.has(id)) {
              memberMap.set(id, {
                id,
                display_name: names[idx] || id,
              });
            }
          });
          // Also include manager if they want to track themselves
          if (team.manager_user_id && !memberMap.has(team.manager_user_id)) {
            memberMap.set(team.manager_user_id, {
              id: team.manager_user_id,
              display_name: team.manager_name || team.manager_user_id,
            });
          }
        }

        const members = Array.from(memberMap.values());
        setTeamMembers(members);

        const assignedIds = new Set(assignmentsRes.data.user_ids || []);
        setInitialAssigned(assignedIds);
        setSelected(new Set(assignedIds));
      } catch (e) {
        toast.error('Failed to load team members');
      } finally {
        if (!cancelled) setLoadingMembers(false);
      }
    })();
    return () => { cancelled = true; };
  }, [project.id]); // eslint-disable-line

  const toggle = (userId) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const allMemberIds = new Set(teamMembers.map(m => m.id));

      // assign_user_ids: newly checked (in selected but not in initialAssigned)
      const assign_user_ids = [...selected].filter(id => !initialAssigned.has(id));

      // unassign_user_ids: unchecked team members (were in initialAssigned but now not in selected)
      const unassign_user_ids = [...initialAssigned].filter(id => !selected.has(id) && allMemberIds.has(id));

      if (assign_user_ids.length === 0 && unassign_user_ids.length === 0) {
        toast.info('No changes to save');
        onClose();
        return;
      }

      await api.post('/api/project-tracking/assign-to-users', {
        project_id: project.id,
        assign_user_ids,
        unassign_user_ids,
        project_name: project.name || '',
        project_stage: project.stage || '',
        project_value: project.encon_value || project.barbour_value || null,
        crm_url: project.crm_url || '',
      });

      const msgs = [];
      if (assign_user_ids.length > 0) msgs.push(`assigned to ${assign_user_ids.length} member(s)`);
      if (unassign_user_ids.length > 0) msgs.push(`removed from ${unassign_user_ids.length} member(s)`);
      toast.success(`Project ${msgs.join(', ')}`);
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save assignments');
    } finally {
      setSaving(false);
    }
  };

  const fmtValue = (v) => v ? `£${Number(v).toLocaleString('en-GB', { maximumFractionDigits: 0 })}` : null;
  const projectValue = fmtValue(project.encon_value || project.barbour_value);

  return (
    <div className="fixed inset-0 z-[130] flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md flex flex-col" style={{ maxHeight: 'min(85vh, 560px)' }}>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${ORANGE}18` }}>
              <Users className="w-4 h-4" style={{ color: ORANGE }} />
            </div>
            <div>
              <h3 className="text-sm font-bold text-gray-900">Assign to Team Members</h3>
              <p className="text-[11px] text-gray-400 truncate max-w-[260px]">{project.name}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        {/* Project meta */}
        <div className="px-5 py-2.5 bg-orange-50/50 border-b border-orange-100 flex items-center gap-3 flex-shrink-0">
          {project.stage && (
            <span className="text-[11px] px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 font-semibold">
              {project.stage}
            </span>
          )}
          {projectValue && (
            <span className="text-xs font-bold" style={{ color: ORANGE }}>{projectValue}</span>
          )}
          <span className="text-[11px] text-gray-400 ml-auto">Check members to add to their Projects tab</span>
        </div>

        {/* Member list */}
        <div className="flex-1 overflow-y-auto px-5 py-3">
          {loadingMembers ? (
            <div className="flex items-center justify-center py-12 gap-2 text-gray-400">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-sm">Loading team members…</span>
            </div>
          ) : teamMembers.length === 0 ? (
            <div className="text-center py-10 text-gray-400 text-sm">
              No team members found. Configure teams first.
            </div>
          ) : (
            <div className="space-y-1.5">
              {teamMembers.map(member => {
                const checked = selected.has(member.id);
                const wasAssigned = initialAssigned.has(member.id);
                return (
                  <button
                    key={member.id}
                    type="button"
                    onClick={() => toggle(member.id)}
                    data-testid={`assign-member-${member.id}`}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border-2 transition-all text-left ${
                      checked
                        ? 'border-orange-400 bg-orange-50'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                      checked ? 'border-orange-500 bg-orange-500' : 'border-gray-300'
                    }`}>
                      {checked && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <span className={`text-sm font-semibold flex-1 ${checked ? 'text-orange-700' : 'text-gray-700'}`}>
                      {member.display_name}
                    </span>
                    {wasAssigned && !checked && (
                      <span className="text-[10px] text-red-400 font-medium">will remove</span>
                    )}
                    {!wasAssigned && checked && (
                      <span className="text-[10px] font-medium" style={{ color: ORANGE }}>will add</span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center gap-2 px-5 py-3.5 border-t border-gray-100 flex-shrink-0">
          <span className="text-xs text-gray-400 flex-1">
            {selected.size} member{selected.size !== 1 ? 's' : ''} selected
          </span>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-xl border-2 border-gray-200 text-gray-600 text-sm font-semibold hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || loadingMembers}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-white text-sm font-semibold disabled:opacity-50 hover:opacity-90 transition-opacity"
            style={{ background: ORANGE }}
            data-testid="assign-save-btn"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Users className="w-3.5 h-3.5" />}
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
